import get from 'lodash/get'

/**
 * Which inflation rate a joint chart discounts by.
 *
 * A single client's present value is unambiguous: their plan carries one rate.
 * A joint chart has two plans and one line, and until now resolved that by
 * accident — constructDataPointsForJointScenario was called without its props,
 * so its rate was the 0.06 default for every couple whatever they had chosen.
 *
 * The rule, in order:
 *
 *   1. Both partners on the same rate — use it. Nothing to resolve.
 *   2. They differ and one is simply following the system rate — that partner
 *      has expressed no preference, so the other's rate is the only one anyone
 *      chose. Use it.
 *   3. They differ and both chose — there is no answer that is right for both.
 *      Use the higher, which discounts harder and so understates the couple's
 *      capital rather than flattering it, and say so on the chart.
 *
 * "The system rate" is read from the payload rather than written down here.
 * The default is an editable global assumption, so a literal 0.06 would quietly
 * stop meaning "the default" the day someone changes it.
 */

// Only for a payload that predates systemInflation being serialized. The live
// value comes from AssumptionsService.
export const FALLBACK_SYSTEM_INFLATION = 0.06

export const JointInflationReason = {
  agreed: 'agreed',
  oneFollowsSystem: 'one_follows_system',
  bothChose: 'both_chose',
  unknown: 'unknown'
}

const participantInflation = (participantQuery) => {
  const assumptions = get(participantQuery, 'scenario.data.baseScenario.assumptions')
  const inflation = get(assumptions, 'inflation')

  return typeof inflation === 'number' ? inflation : null
}

const participantSystemInflation = (participantQuery) => {
  const assumptions = get(participantQuery, 'scenario.data.baseScenario.assumptions')
  const systemInflation = get(assumptions, 'systemInflation')

  return typeof systemInflation === 'number' ? systemInflation : null
}

/**
 * @returns {{
 *   rate: number,
 *   reason: string,
 *   systemInflation: number,
 *   chosenBy: ?string,
 *   participants: Array<{ name: ?string, rate: number, followsSystem: boolean }>
 * }}
 */
export const resolveJointInflation = (participantsQueries) => {
  const queries = participantsQueries || []
  const systemInflation = queries.map(participantSystemInflation).find((rate) => rate !== null)
    ?? FALLBACK_SYSTEM_INFLATION

  const participants = queries
    .map((participantQuery) => ({
      name: get(participantQuery, 'participant.preferredName') || get(participantQuery, 'participant.name'),
      rate: participantInflation(participantQuery)
    }))
    .filter((participant) => participant.rate !== null)
    .map((participant) => ({ ...participant, followsSystem: participant.rate === systemInflation }))

  const resolution = { systemInflation, participants }

  if (participants.length === 0) {
    return { ...resolution, rate: systemInflation, reason: JointInflationReason.unknown, chosenBy: null }
  }

  const rates = participants.map((participant) => participant.rate)

  if (new Set(rates).size === 1) {
    return { ...resolution, rate: rates[0], reason: JointInflationReason.agreed, chosenBy: null }
  }

  const chosen = participants.filter((participant) => !participant.followsSystem)
  const distinctChosen = new Set(chosen.map((participant) => participant.rate))

  // Rule 2. One partner is following the system rate, so the other's is the
  // only rate anybody picked.
  if (distinctChosen.size === 1) {
    return {
      ...resolution,
      rate: chosen[0].rate,
      reason: JointInflationReason.oneFollowsSystem,
      chosenBy: chosen[0].name
    }
  }

  // Rule 3. Both picked, and differently. The higher rate is the cautious one.
  const highest = chosen.reduce((a, b) => (b.rate > a.rate ? b : a))

  return {
    ...resolution,
    rate: highest.rate,
    reason: JointInflationReason.bothChose,
    chosenBy: highest.name
  }
}

export const formatInflation = (rate) => `${Number((rate * 100).toFixed(2))}%`

/**
 * One sentence saying which rate the chart used and why. Present value that
 * does not name its rate is the reason this was invisible for so long: two
 * charts discounted differently look like two different forecasts.
 */
export const describeJointInflation = (resolution) => {
  const { rate, reason, participants, chosenBy, systemInflation } = resolution
  const others = participants.filter((participant) => participant.name !== chosenBy)
  const named = (participant) => `${participant.name} ${formatInflation(participant.rate)}`

  switch (reason) {
    case JointInflationReason.agreed:
      return `Present value at ${formatInflation(rate)} — both plans use the same inflation.`

    case JointInflationReason.oneFollowsSystem:
      return `Present value at ${formatInflation(rate)}, ${chosenBy}’s rate. ` +
        `${others.map((participant) => participant.name).join(' and ')} ` +
        `${others.length === 1 ? 'follows' : 'follow'} the system rate of ${formatInflation(systemInflation)}.`

    case JointInflationReason.bothChose:
      return `Present value at ${formatInflation(rate)}, ${chosenBy}’s — the higher of two chosen rates ` +
        `(${others.map(named).join(', ')}). The higher rate is used so the joint capital is not flattered.`

    default:
      return `Present value at the system rate of ${formatInflation(rate)} — neither plan states one.`
  }
}
